// hooks/useActivityLogger.js
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import OfflineDb from "../db";

/* ────────────────────────────────────────────────────────────
   GLOBALS (persist across hook calls)
────────────────────────────────────────────────────────────── */
let dbInstance = null;

/* ────────────────────────────────────────────────────────────
   OPEN DB (cached)
────────────────────────────────────────────────────────────── */
async function openDB() {
  if (dbInstance) return dbInstance;

  dbInstance = await new Promise((resolve, reject) => {
    const req = indexedDB.open("UserActivityDB", 1);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("userActivityLogs")) {
        db.createObjectStore("userActivityLogs", {
          keyPath: "id",
          autoIncrement: true,
        });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  return dbInstance;
}

/* ────────────────────────────────────────────────────────────
   LIGHTWEIGHT SANITIZER
────────────────────────────────────────────────────────────── */
function sanitize(obj) {
  const out = {};
  for (const k in obj) {
    const v = obj[k];
    if (v == null) continue;
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    ) {
      out[k] = v;
    }
  }
  return out;
}

/* ────────────────────────────────────────────────────────────
   ADD ACTIVITY
────────────────────────────────────────────────────────────── */
async function storeActivity(activity, userId) {
  if (!userId) return;

  const db = await openDB();
  const tx = db.transaction("userActivityLogs", "readwrite");
  tx.objectStore("userActivityLogs").add({
    ...sanitize(activity),
    uid: userId,
    timestamp: new Date().toISOString(),
    source: "browser",
    saved: false,
  });
}

/* ────────────────────────────────────────────────────────────
   GET PATH (works for hash + browser)
────────────────────────────────────────────────────────────── */
function getPath(loc) {
  if (loc?.pathname) {
    return loc.pathname + (loc.search || "") + (loc.hash || "");
  }
  return (
    window.location.hash?.replace(/^#/, "") || window.location.pathname || "/"
  );
}

/* ────────────────────────────────────────────────────────────
   MAIN HOOK
────────────────────────────────────────────────────────────── */
export function useActivityLogger() {
  const location = useLocation();

  const lastPathRef = useRef("/");
  const lastTimeRef = useRef(Date.now());
  const userIdRef = useRef(null);
  const userRef = useRef(null);
  const initialized = useRef(false);

  const currentPath = getPath(location);

  /* ────────────────────────────────────────────────────────────
     Load user once
  ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const loginDetails = await OfflineDb.getDataFromPouchDB("loginDetails");
        userIdRef.current = loginDetails?.data?.userCredentials?.id || null;
        userRef.current = loginDetails.data;
      } catch (err) {
        console.error("Failed to fetch login details:", err);
      }
    })();
  }, []);

  /* ────────────────────────────────────────────────────────────
     Init listeners once
  ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    lastPathRef.current = currentPath;

    storeActivity({ type: "page-load", path: currentPath }, userIdRef.current);

    const clickHandler = (e) => {
      const t = e.target.closest(
        "[data-track],button,a,input,textarea,select,label"
      );
      if (!t) return;

      storeActivity(
        {
          type: "click",
          element: t.tagName,
          class: t.className || "",
          text: (t.innerText || t.value || "").slice(0, 100),
          path: getPath(location),
        },
        userIdRef.current
      );
    };

    const unloadHandler = () => {
      const now = Date.now();
      const diff = now - lastTimeRef.current;

      if (diff > 500) {
        storeActivity(
          {
            type: "time-spent",
            path: lastPathRef.current,
            durationMs: diff,
            durationSec: Math.round(diff / 1000),
          },
          userIdRef.current
        );
      }
    };

    document.addEventListener("click", clickHandler, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchend", clickHandler, {
      capture: true,
      passive: true,
    });
    window.addEventListener("beforeunload", unloadHandler);

    return () => {
      document.removeEventListener("click", clickHandler, true);
      document.removeEventListener("touchend", clickHandler, true);
      window.removeEventListener("beforeunload", unloadHandler);
    };
  }, []);

  /* ────────────────────────────────────────────────────────────
     Log route changes
  ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const now = Date.now();
    const diff = now - lastTimeRef.current;

    if (diff > 500) {
      storeActivity(
        {
          type: "time-spent",
          path: lastPathRef.current,
          durationMs: diff,
          durationSec: Math.round(diff / 1000),
        },
        userIdRef.current
      );
    }

    storeActivity(
      {
        type: "navigation",
        path: currentPath,
        from: lastPathRef.current,
      },
      userIdRef.current
    );

    lastPathRef.current = currentPath;
    lastTimeRef.current = Date.now();
  }, [location.pathname, location.search, location.hash]);

  /* ────────────────────────────────────────────────────────────
     Custom logger
  ────────────────────────────────────────────────────────────── */
  const logCustomActivity = (data) => {
    storeActivity(
      {
        type: "custom",
        path: currentPath,
        ...data,
      },
      userIdRef.current
    );
  };

  return { logCustomActivity, sessionUserBoValue: userRef.current };
}
